import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  SelectChangeEvent,
  Tooltip,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import config from '../config';
import { allServerNames, getCurrentServerName, getServerMergeHistory, getFinalServerName } from '../data/servers';
import { getAllRelatedServers } from '../utils/serverUtils';
import ServerDropdown from './ServerDropdown';
import AddIcon from '@mui/icons-material/Add';
import { debounce } from 'lodash';

const classes = [
  'Bard', 'Beastlord', 'Berserker', 'Cleric', 'Druid', 'Enchanter', 'Magician', 'Monk',
  'Necromancer', 'Paladin', 'Ranger', 'Rogue', 'Shadow Knight', 'Shaman', 'Warrior', 'Wizard'
] as const;

const races = [
  'Barbarian', 'Dark Elf', 'Drakkin', 'Dwarf', 'Erudite', 'Froglok', 'Gnome', 'Half Elf',
  'Halfling', 'High Elf', 'Human', 'Iksar', 'Ogre', 'Troll', 'Vah Shir', 'Wood Elf'
] as const;

// Race to available classes mapping
const raceClassMap: { [key: string]: string[] } = {
  'Barbarian': ['Beastlord', 'Berserker', 'Rogue', 'Shaman', 'Warrior'],
  'Dark Elf': ['Cleric', 'Enchanter', 'Magician', 'Necromancer', 'Rogue', 'Shadow Knight', 'Warrior', 'Wizard'],
  'Drakkin': ['Bard', 'Cleric', 'Druid', 'Enchanter', 'Magician', 'Monk', 'Necromancer', 'Paladin', 'Ranger', 'Rogue', 'Shadow Knight', 'Warrior', 'Wizard'],
  'Dwarf': ['Berserker', 'Cleric', 'Paladin', 'Rogue', 'Warrior'],
  'Erudite': ['Cleric', 'Enchanter', 'Magician', 'Necromancer', 'Paladin', 'Shadow Knight', 'Wizard'],
  'Froglok': ['Cleric', 'Monk', 'Necromancer', 'Rogue', 'Shadow Knight', 'Shaman', 'Warrior', 'Wizard'],
  'Gnome': ['Cleric', 'Enchanter', 'Magician', 'Necromancer', 'Paladin', 'Rogue', 'Shadow Knight', 'Warrior', 'Wizard'],
  'Half Elf': ['Bard', 'Druid', 'Paladin', 'Ranger', 'Rogue', 'Warrior'],
  'Halfling': ['Cleric', 'Druid', 'Paladin', 'Ranger', 'Rogue', 'Warrior'],
  'High Elf': ['Cleric', 'Enchanter', 'Magician', 'Paladin', 'Wizard'],
  'Human': ['Bard', 'Cleric', 'Druid', 'Enchanter', 'Magician', 'Monk', 'Necromancer', 'Paladin', 'Ranger', 'Rogue', 'Shadow Knight', 'Warrior', 'Wizard'],
  'Iksar': ['Beastlord', 'Monk', 'Necromancer', 'Shadow Knight', 'Shaman', 'Warrior'],
  'Ogre': ['Beastlord', 'Berserker', 'Shadow Knight', 'Shaman', 'Warrior'],
  'Troll': ['Beastlord', 'Berserker', 'Shadow Knight', 'Shaman', 'Warrior'],
  'Vah Shir': ['Bard', 'Beastlord', 'Berserker', 'Rogue', 'Shaman', 'Warrior'],
  'Wood Elf': ['Bard', 'Beastlord', 'Druid', 'Ranger', 'Rogue', 'Warrior']
};

interface Guild {
  _id: string;
  name: string;
  server: string;
}

interface GuildEntry {
  _id?: string;
  name: string;
  startDate?: {
    month?: number;
    year?: number;
  };
  endDate?: {
    month?: number;
    year?: number;
  };
  searchInput?: string;
}

interface FormData {
  characterName: string;
  characterSurname: string;
  mageloLink: string;
  server: string;
  guilds: GuildEntry[];
  class: string;
  race: string;
  level: number;
  displayedGuildId?: string | null;
  startDate?: {
    month?: number;
    year?: number;
  };
  endDate?: {
    month?: number;
    year?: number;
  };
}

interface Character {
  _id: string;
  characterName: string;
  characterSurname: string;
  mageloLink: string;
  server: string;
  class: string;
  race: string;
  level: number;
  guilds: GuildEntry[];
  displayedGuildId?: string | null;
  startDate?: {
    month?: number;
    year?: number;
  };
  endDate?: {
    month?: number;
    year?: number;
  };
}

interface CharacterFormProps {
  initialCharacter?: Character;
  isEditing?: boolean;
}

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const CharacterForm: React.FC<CharacterFormProps> = ({ initialCharacter, isEditing = false }) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cachedToken, setCachedToken] = useState<string | null>(null);
  const [availableClasses, setAvailableClasses] = useState<string[]>(() => {
    // Initialize with classes for the initial character's race if it exists
    if (initialCharacter?.race) {
      return raceClassMap[initialCharacter.race] || [];
    }
    return [];
  });
  const [formData, setFormData] = useState<FormData>({
    characterName: '',
    characterSurname: '',
    mageloLink: '',
    server: '',
    guilds: [{ name: '', searchInput: '' }],
    class: '',
    race: '',
    level: 1,
    displayedGuildId: null
  });

  // Initialize available classes when component mounts with initial character
  useEffect(() => {
    if (initialCharacter?.race) {
      setAvailableClasses(raceClassMap[initialCharacter.race] || []);
    }
  }, [initialCharacter]);

  // Guild-related state
  const [guildDialogOpen, setGuildDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [newGuildName, setNewGuildName] = useState('');
  const [newGuildServer, setNewGuildServer] = useState('');
  const [guildOptions, setGuildOptions] = useState<Guild[]>([]);
  const [guildSearchInput, setGuildSearchInput] = useState('');
  const [guildError, setGuildError] = useState<string | null>(null);
  const [guildValidationStates, setGuildValidationStates] = useState<boolean[]>([]);
  const [guildNameError, setGuildNameError] = useState<string | null>(null);

  // Function to get token, using cached version if available
  const getToken = useCallback(async () => {
    if (cachedToken) return cachedToken;
    
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: config.auth0.audience,
          scope: 'openid profile email'
        }
      });
      setCachedToken(token);
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      throw error;
    }
  }, [getAccessTokenSilently, cachedToken]);

  // Function to set validation state for a specific guild index
  const setGuildValidation = (index: number, isValid: boolean) => {
    setGuildValidationStates(prev => {
      const newStates = [...prev];
      newStates[index] = isValid;
      return newStates;
    });
  };

  // Function to check if a guild is valid
  const isGuildValid = (index: number): boolean => {
    return guildValidationStates[index] || false;
  };

  // Generate array of years from 1999 to current year
  const years = Array.from({ length: new Date().getFullYear() - 1998 }, (_, i) => 1999 + i);

  useEffect(() => {
    // Update player name when display name changes
    const handleDisplayNameChange = () => {
      const displayName = localStorage.getItem('displayName') || '';
      setFormData(prev => ({ ...prev, playerName: displayName }));
    };

    window.addEventListener('displayNameChanged', handleDisplayNameChange);
    return () => {
      window.removeEventListener('displayNameChanged', handleDisplayNameChange);
    };
  }, []);

  // Update available classes when race changes
  useEffect(() => {
    if (formData.race) {
      const classesForRace = raceClassMap[formData.race] || [];
      setAvailableClasses(classesForRace);
      
      // Only reset class if it's not valid for the new race and we're not initializing with a valid class
      if (!classesForRace.includes(formData.class) && !(initialCharacter?.race === formData.race && initialCharacter?.class === formData.class)) {
        setFormData(prev => ({ ...prev, class: '' }));
      }
    } else {
      setAvailableClasses([]);
    }
  }, [formData.race, initialCharacter]);

  // Load guilds when server changes
  useEffect(() => {
    const loadGuildsForServer = async () => {
      if (!formData.server) {
        setGuildOptions([]);
        return;
      }

      try {
        const token = await getToken();
        const relatedServers = Array.from(getAllRelatedServers(formData.server));
        
        const response = await axios.get<Guild[]>(`${config.api.baseURL}/api/guilds/search`, {
          params: {
            server: formData.server,
            relatedServers: relatedServers.join(',')
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setGuildOptions(response.data);
      } catch (error) {
        console.error('Error loading guilds for server:', error);
        setGuildOptions([]);
      }
    };

    loadGuildsForServer();
  }, [formData.server, getToken]);

  // Client-side guild filtering based on input
  const filterGuildOptions = useCallback((searchValue: string): Guild[] => {
    if (!searchValue) return guildOptions;
    const searchLower = searchValue.toLowerCase();
    return guildOptions.filter(guild => {
      const nameMatch = guild.name.toLowerCase().includes(searchLower);
      const isRelatedServer = getAllRelatedServers(formData.server).has(guild.server);
      return nameMatch && isRelatedServer;
    });
  }, [guildOptions, formData.server]);

  // Batch guild validations
  const validateGuilds = useCallback(async (guildsToValidate: GuildEntry[], server: string) => {
    if (!server || guildsToValidate.length === 0) return;

    try {
      const token = await getToken();
      const uniqueGuildNames = Array.from(new Set(guildsToValidate.map(g => g.name))).filter(Boolean);
      
      if (uniqueGuildNames.length === 0) return;

      const response = await axios.get<Guild[]>(`${config.api.baseURL}/api/guilds/search`, {
        params: {
          names: uniqueGuildNames.join(','),
          server: server
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const validGuilds = response.data;
      const newValidationStates = guildsToValidate.map(guild => 
        validGuilds.some((validGuild: Guild) => validGuild.name === guild.name)
      );
      setGuildValidationStates(newValidationStates);
      setGuildOptions(validGuilds);
    } catch (error) {
      console.error('Error validating guilds:', error);
    }
  }, [getToken]);

  const validateGuildName = (name: string): boolean => {
    if (name.length > 52) {
      setGuildNameError('Guild name must be 52 characters or less');
      return false;
    }
    if (!/^[a-zA-Z\s]*$/.test(name)) {
      setGuildNameError('Guild name can only contain letters and spaces');
      return false;
    }
    setGuildNameError(null);
    return true;
  };

  const handleCreateGuild = async () => {
    try {
      setGuildError(null);
      
      // Validate guild name before proceeding
      if (!validateGuildName(newGuildName)) {
        return;
      }
      
      const token = await getToken();
      
      const response = await axios.post(
        `${config.api.baseURL}/api/guilds`,
        {
          name: newGuildName,
          server: newGuildServer
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const newGuild = response.data;

      // Add the new guild to options
      setGuildOptions(prev => [...prev, newGuild]);

      // Find the guild entry that matches the created guild's name and update it
      setFormData(prev => {
        const newGuilds = prev.guilds.map(guild => {
          if (guild.searchInput === newGuildName) {
            return { 
              ...guild, 
              name: newGuild.name,
              _id: newGuild._id 
            };
          }
          return guild;
        });

        // If this is the first guild, set it as the displayed guild
        const updatedDisplayedGuildId = prev.displayedGuildId || 
          (newGuilds[0]?._id === newGuild._id ? newGuild._id : prev.displayedGuildId);

        return { 
          ...prev, 
          guilds: newGuilds,
          displayedGuildId: updatedDisplayedGuildId
        };
      });
      
      // Dispatch guild-added event to update stats
      window.dispatchEvent(new CustomEvent('guild-added'));
      
      // Close dialogs and reset form
      setConfirmDialogOpen(false);
      setGuildDialogOpen(false);
      setNewGuildName('');
      setNewGuildServer('');
      setGuildNameError(null);
    } catch (error: any) {
      console.error('Error creating guild:', error);
      setGuildError(error.response?.data?.error || 'Failed to create guild');
    }
  };

  const [nameError, setNameError] = useState<string | null>(null);
  const [surnameError, setSurnameError] = useState<string | null>(null);

  const validateName = (name: string): boolean => {
    if (name.length > 18) {
      setNameError('Name must be 18 characters or less');
      return false;
    }
    if (!/^[a-zA-Z]*$/.test(name)) {
      setNameError('Name can only contain letters');
      return false;
    }
    setNameError(null);
    return true;
  };

  const validateSurname = (surname: string): boolean => {
    if (surname.length > 35) {
      setSurnameError('Surname must be 35 characters or less');
      return false;
    }
    if (!/^[a-zA-Z\s'",`]*$/.test(surname)) {
      setSurnameError('Surname can only contain letters, spaces, quotes, and commas');
      return false;
    }
    setSurnameError(null);
    return true;
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    if (name) {
      if (name === 'characterName') {
        validateName(value);
      } else if (name === 'characterSurname') {
        validateSurname(value);
      }
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (name) {
      setFormData(prev => ({
        ...prev,
        // Convert empty string to null for displayedGuildId
        [name]: name === 'displayedGuildId' && value === '' ? null : value
      }));
    }
  };

  const handleServerChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      server: value
    }));
  };

  // Add validation for server selection
  const isValidServer = (serverName: string): boolean => {
    // Empty string is valid (for initial state)
    if (serverName === '') {
      return true;
    }

    // Check if it's in allServerNames
    return allServerNames.includes(serverName);
  };

  const getServerTooltip = (serverName: string): string => {
    const currentName = getCurrentServerName(serverName);
    if (currentName === serverName) return '';

    const mergeHistory = getServerMergeHistory(serverName);
    if (!mergeHistory?.length) return `Now part of ${currentName}`;

    const history = mergeHistory
      .map(m => `${m.originalServer} (${m.mergeDate})`)
      .join('\n');
    return `Now part of ${currentName}\nMerge History:\n${history}`;
  };

  const handleGuildChange = (index: number, field: keyof GuildEntry | 'startMonth' | 'startYear' | 'endMonth' | 'endYear', value: any) => {
    setFormData(prev => {
      const newGuilds = [...prev.guilds];
      if (field === 'startMonth' || field === 'startYear') {
        // Initialize startDate if it doesn't exist
        if (!newGuilds[index].startDate) {
          newGuilds[index].startDate = { month: undefined, year: undefined };
        }
        
        // Handle year changes
        if (field === 'startYear') {
          if (!value) {
            newGuilds[index].startDate = undefined;
          } else {
            const startDate = { ...newGuilds[index].startDate! };
            startDate.year = value;
            newGuilds[index].startDate = startDate;
          }
        }
        
        // Handle month changes
        if (field === 'startMonth') {
          if (newGuilds[index].startDate?.year) {
            const startDate = { ...newGuilds[index].startDate };
            startDate.month = value || undefined;
            newGuilds[index].startDate = startDate;
          }
        }
        
        // Remove startDate if both fields are empty
        if (!newGuilds[index].startDate?.month && !newGuilds[index].startDate?.year) {
          newGuilds[index].startDate = undefined;
        }
      } else if (field === 'endMonth' || field === 'endYear') {
        // Initialize endDate if it doesn't exist
        if (!newGuilds[index].endDate) {
          newGuilds[index].endDate = { month: undefined, year: undefined };
        }
        
        // Handle year changes
        if (field === 'endYear') {
          if (!value) {
            newGuilds[index].endDate = undefined;
          } else {
            const endDate = { ...newGuilds[index].endDate! };
            endDate.year = value;
            newGuilds[index].endDate = endDate;
          }
        }
        
        // Handle month changes
        if (field === 'endMonth') {
          if (newGuilds[index].endDate?.year) {
            const endDate = { ...newGuilds[index].endDate };
            endDate.month = value || undefined;
            newGuilds[index].endDate = endDate;
          }
        }
        
        // Remove endDate if both fields are empty
        if (!newGuilds[index].endDate?.month && !newGuilds[index].endDate?.year) {
          newGuilds[index].endDate = undefined;
        }
      } else {
        newGuilds[index] = {
          ...newGuilds[index],
          [field]: value
        };
      }
      return { ...prev, guilds: newGuilds };
    });
  };

  const addGuildEntry = () => {
    setFormData(prev => ({
      ...prev,
      guilds: [...prev.guilds, { 
        name: '', 
        searchInput: ''
      }]
    }));
  };

  const removeGuildEntry = (index: number) => {
    setFormData(prev => ({
      ...prev,
      guilds: prev.guilds.filter((_, i) => i !== index)
    }));
  };

  // Initialize form data when editing
  useEffect(() => {
    const initializeFormData = async () => {
      if (initialCharacter) {
        const guilds = initialCharacter.guilds?.map((g: any) => {
          const guildData = g.guildId || g;
          return {
            _id: guildData._id || '',
            name: guildData.name || '',
            searchInput: guildData.name || '',
            startDate: {
              month: g.startDate?.month || undefined,
              year: g.startDate?.year || undefined
            },
            endDate: {
              month: g.endDate?.month || undefined,
              year: g.endDate?.year || undefined
            }
          };
        }) || [{ name: '', searchInput: '' }];
        
        setFormData({
          characterName: initialCharacter.characterName || '',
          characterSurname: initialCharacter.characterSurname || '',
          mageloLink: initialCharacter.mageloLink || '',
          server: initialCharacter.server || '',
          guilds,
          class: initialCharacter.class || '',
          race: initialCharacter.race || '',
          level: initialCharacter.level || 1,
          displayedGuildId: initialCharacter.displayedGuildId || guilds[0]?._id || '',
          startDate: initialCharacter.startDate,
          endDate: initialCharacter.endDate
        });

        // Validate guilds in a single batch request
        if (initialCharacter.server && guilds.length > 0) {
          await validateGuilds(guilds, initialCharacter.server);
        }
      }
    };

    initializeFormData();
  }, [initialCharacter, isEditing, validateGuilds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate character name and surname before submitting
    if (!validateName(formData.characterName)) {
      return;
    }
    if (formData.characterSurname && !validateSurname(formData.characterSurname)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const token = await getToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      if (isEditing && initialCharacter?._id) {
        await axios.put(`${config.api.baseURL}/api/characters/${initialCharacter._id}`, formData, { headers });
        window.dispatchEvent(new CustomEvent('character-updated'));
      } else {
        await axios.post(`${config.api.baseURL}/api/characters`, formData, { headers });
        window.dispatchEvent(new CustomEvent('character-added'));
      }
      
      // Navigate to My Characters page
      navigate('/my-characters');
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} character:`, error);
      if (axios.isAxiosError(error)) {
        setError(error.response?.data?.error || `Failed to ${isEditing ? 'update' : 'create'} character. Please try again.`);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  return (
    <Paper elevation={3}>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            {isEditing ? 'Edit Character' : 'Add New Character'}
          </Typography>
          <Box width="50%">
            <TextField
              fullWidth
              label="Magelo Link"
              name="mageloLink"
              value={formData.mageloLink}
              onChange={handleTextChange}
              size="small"
            />
          </Box>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Character Name, Surname, and Level */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Character Name"
                name="characterName"
                value={formData.characterName}
                onChange={handleTextChange}
                required
                error={!!nameError}
                helperText={nameError}
                inputProps={{ maxLength: 18 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Character Surname"
                name="characterSurname"
                value={formData.characterSurname}
                onChange={handleTextChange}
                error={!!surnameError}
                helperText={surnameError}
                inputProps={{ maxLength: 35 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Level"
                name="level"
                value={formData.level}
                onChange={handleTextChange}
                inputProps={{ min: 1, max: 125 }}
                required
              />
            </Grid>

            {/* Server and Years */}
            <Grid item xs={12} sm={4}>
              <ServerDropdown
                value={formData.server}
                onChange={handleServerChange}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  select
                  label="Start Year"
                  name="startYear"
                  value={formData.startDate?.year || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    startDate: {
                      ...prev.startDate,
                      year: Number(e.target.value) || undefined
                    }
                  }))}
                  helperText="Optional"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  select
                  label="End Year"
                  name="endYear"
                  value={formData.endDate?.year || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    endDate: {
                      ...prev.endDate,
                      year: Number(e.target.value) || undefined
                    }
                  }))}
                  helperText="Optional"
                  error={formData.endDate?.year !== undefined && formData.startDate?.year !== undefined && formData.endDate.year < formData.startDate.year}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Grid>

            {/* Race, Class, and Displayed Guild */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Displayed Guild</InputLabel>
                <Select
                  value={formData.displayedGuildId || ''}
                  onChange={handleSelectChange}
                  name="displayedGuildId"
                  label="Displayed Guild"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {formData.guilds
                    .filter(guild => guild.name && guild._id) // Only show guilds that have both a name and an ID
                    .map((guild) => (
                      <MenuItem key={guild._id} value={guild._id}>
                        {guild.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Race"
                name="race"
                value={formData.race}
                onChange={handleTextChange}
                required
              >
                {races.map((race: string) => (
                  <MenuItem key={race} value={race}>
                    {race}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Class"
                name="class"
                value={formData.class}
                onChange={handleTextChange}
                required
                disabled={!formData.race}
              >
                {availableClasses.map((className: string) => (
                  <MenuItem key={className} value={className}>
                    {className}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Guild History */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Guild History
              </Typography>
              {formData.guilds.map((guild, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Autocomplete
                        freeSolo
                        options={filterGuildOptions(guild.searchInput || '')}
                        getOptionLabel={(option: Guild | string): string => 
                          typeof option === 'string' ? option : option.name
                        }
                        value={guild.name || null}
                        inputValue={guild.searchInput || ''}
                        onInputChange={(_, newInputValue, reason) => {
                          // Only update if it's not from selecting an option
                          if (reason === 'input' || reason === 'clear') {
                            const newGuilds = [...formData.guilds];
                            newGuilds[index] = {
                              ...newGuilds[index],
                              searchInput: newInputValue,
                              name: newInputValue,
                              _id: '' // Clear ID when manually typing
                            };
                            setFormData(prev => ({
                              ...prev,
                              guilds: newGuilds
                            }));
                          }
                        }}
                        onChange={(_, newValue) => {
                          const newGuilds = [...formData.guilds];
                          if (typeof newValue === 'string') {
                            newGuilds[index] = {
                              ...newGuilds[index],
                              name: newValue,
                              searchInput: newValue,
                              _id: ''
                            };
                          } else if (newValue) {
                            newGuilds[index] = {
                              ...newGuilds[index],
                              name: newValue.name,
                              searchInput: newValue.name,
                              _id: newValue._id
                            };
                          } else {
                            newGuilds[index] = {
                              ...newGuilds[index],
                              name: '',
                              searchInput: '',
                              _id: ''
                            };
                          }
                          setFormData(prev => ({
                            ...prev,
                            guilds: newGuilds
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Guild"
                            placeholder="Search for a guild"
                            error={/\d/.test(guild.searchInput || '')}
                            helperText={/\d/.test(guild.searchInput || '') ? 
                              "Guild name cannot contain numbers" : 
                              (guild.searchInput && guildOptions.length === 0 ? 
                                "Guild not found - use Create button to make a new guild" : "")}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {guildOptions.some(g => g.name.toLowerCase() === (guild.searchInput || '').toLowerCase()) && (
                                    <InputAdornment position="end">
                                      <CheckCircleIcon color="success" />
                                    </InputAdornment>
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            disabled={!formData.server}
                          />
                        )}
                        disabled={!formData.server}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setNewGuildServer(formData.server);
                          setNewGuildName(guild.searchInput || '');
                          setGuildDialogOpen(true);
                        }}
                        disabled={!formData.server || guildOptions.some(g => g.name.toLowerCase() === (guild.searchInput || '').toLowerCase()) || /\d/.test(guild.searchInput || '')}
                        fullWidth
                        sx={{ height: '56px' }}
                      >
                        Create
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box display="flex" gap={1}>
                        <TextField
                          select
                          fullWidth
                          label="Start Year"
                          value={guild.startDate?.year || ''}
                          onChange={(e) => handleGuildChange(index, 'startYear', Number(e.target.value))}
                          size="medium"
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {years.map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          fullWidth
                          label="Start Month"
                          value={guild.startDate?.month || ''}
                          onChange={(e) => handleGuildChange(index, 'startMonth', Number(e.target.value))}
                          size="medium"
                          disabled={!guild.startDate?.year}
                          helperText={guild.startDate?.month && !guild.startDate?.year ? "Select year first" : ""}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {months.map((month, i) => (
                            <MenuItem key={month} value={i + 1}>
                              {month}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box display="flex" gap={1}>
                        <TextField
                          select
                          fullWidth
                          label="End Year"
                          value={guild.endDate?.year || ''}
                          onChange={(e) => handleGuildChange(index, 'endYear', Number(e.target.value))}
                          size="medium"
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {years.map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          fullWidth
                          label="End Month"
                          value={guild.endDate?.month || ''}
                          onChange={(e) => handleGuildChange(index, 'endMonth', Number(e.target.value))}
                          size="medium"
                          disabled={!guild.endDate?.year}
                          helperText={guild.endDate?.month && !guild.endDate?.year ? "Select year first" : ""}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {months.map((month, i) => (
                            <MenuItem key={month} value={i + 1}>
                              {month}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Button
                        color="error"
                        onClick={() => removeGuildEntry(index)}
                        fullWidth
                        sx={{ height: '56px' }}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={addGuildEntry}
                disabled={!formData.server}
                startIcon={<AddIcon />}
              >
                Add Guild
              </Button>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting || (formData.endDate?.year !== undefined && formData.startDate?.year !== undefined && formData.endDate.year < formData.startDate.year)}
              >
                {isSubmitting ? (isEditing ? 'Updating Character...' : 'Adding Character...') : (isEditing ? 'Update Character' : 'Add Character')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>

      {/* Create Guild Dialog */}
      <Dialog 
        open={guildDialogOpen} 
        onClose={() => setGuildDialogOpen(false)}
        PaperProps={{
          sx: { minWidth: '400px' }
        }}
      >
        <DialogTitle>Create New Guild</DialogTitle>
        <DialogContent sx={{ minHeight: '180px', display: 'flex', flexDirection: 'column', pt: 1 }}>
          <Typography color="text.secondary" sx={{ mb: 0.5 }}>
            Please carefully enter the guild name
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 0.5 }}>
            Please select the server the guild was originally created on
          </Typography>
          <Box sx={{ minHeight: '32px', mb: 0.5 }}>
            {guildError && (
              <Alert severity="error">
                {guildError}
              </Alert>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Guild Name"
              value={newGuildName}
              onChange={(e) => {
                const value = e.target.value;
                setNewGuildName(value);
                validateGuildName(value);
              }}
              required
              error={!!guildNameError}
              helperText={guildNameError}
              inputProps={{ maxLength: 52 }}
            />
            <TextField
              fullWidth
              select
              label="Server"
              value={newGuildServer}
              onChange={(e) => setNewGuildServer(e.target.value)}
              required
            >
              {allServerNames.map(serverName => {
                const finalServer = getFinalServerName(serverName);
                return (
                  <MenuItem key={serverName} value={serverName}>
                    {serverName}
                    {finalServer !== serverName && (
                      <span style={{ color: '#666' }}>&nbsp;&nbsp;({finalServer})</span>
                    )}
                  </MenuItem>
                );
              })}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGuildDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => setConfirmDialogOpen(true)}
            variant="contained"
            disabled={!newGuildName || !newGuildServer || !!guildNameError}
          >
            Create Guild
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        PaperProps={{
          sx: { minWidth: '400px' }
        }}
      >
        <DialogTitle>Confirm Guild Creation</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {newGuildServer}: {newGuildName}
          </Typography>
          <Typography>
            This guild name is written correctly.
          </Typography>
          <Typography>
            It is being made on its original server (before merges).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Go Back</Button>
          <Button onClick={handleCreateGuild} variant="contained" color="primary">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CharacterForm; 